/* eslint-disable react/jsx-props-no-spreading */
import App from 'next/app'
import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { GetServerSidePropsContext } from 'next'
import PropTypes from 'prop-types'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ThemeProvider as NextThemesProvider } from 'next-themes'
// eslint-disable-next-line import/no-extraneous-dependencies
import { appWithTranslation, useTranslation } from 'next-i18next'

import Analytics from '@/shared/Analytics'
import ErrorBoundary from '@/shared/ErrorBoundary'
import FreeTrialEnrollment from '@/layout/free-trial-enrollment'
import { FreeTrialBanner } from '@/layout/free-trial-banner'
import { BrandingThemeProvider } from '@/layout/branding-theme'
// app is not using redux for anything anymore
import wrapper from '@/lib/store'

import { fetchAuthCookie, extendAuthCookie, storeAuthCookie } from '@/utils/cookieUtil'
import { getBearerToken, removeTokensFromStorage } from '@/utils/localStorageUtil'
import { assertRequiredEnv } from '@/utils/requiredEnv'
import { resolveLandingRedirect } from '@/utils/landingRouteUtil'
import { isViewAsChangeFromOtherTab } from '@/utils/viewAsUtil'

import nextI18nextConfig from '../next-i18next.config'

import '../stylesheets/main.scss'

// Module scope, so a bundle built without its configuration fails on the first render rather
// than at an arbitrary later click. Next 12 has no server bootstrap hook to use instead.
assertRequiredEnv()

const queryClient = new QueryClient()

// next-themes returns ReactNode but @types/react@17 requires JSX.Element; cast to a valid component type
const ThemeProvider = NextThemesProvider as React.FC<React.PropsWithChildren<Record<string, unknown>>>

// RTL Support for Inputs Only
function RTLSupport() {
  const { i18n } = useTranslation()

  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Only set the data-lang attribute
      document.documentElement.setAttribute('data-lang', i18n.language)
    }
  }, [i18n.language])

  return null
}

// Tokens live in localStorage, shared by every tab: when another tab switches customers, reload so
// this tab's UI and cache match the customer its requests now run as.
function ViewAsTabSync() {
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (isViewAsChangeFromOtherTab(event)) window.location.reload()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return null
}

function SafeHydrate({ children }) {
  if (typeof window !== 'undefined') {
    const router = useRouter()

    if (!(router.asPath.startsWith('/login') || router.asPath.startsWith('/create-account') || router.asPath.startsWith('/almost-there') || router.asPath.startsWith('/reset-password') || router.asPath.startsWith('/invitation') || router.asPath.startsWith('/free-trial') || router.asPath.startsWith('/auth/callback') || router.asPath.startsWith('/auth/embed'))) {
      const authCookie = fetchAuthCookie()

      if (!authCookie) {
        try {
          if (getBearerToken()) {
            storeAuthCookie()
          } else {
            removeTokensFromStorage()
            router.reload()
          }
        } catch {
          removeTokensFromStorage()
          router.reload()
        }
      }
    }
  }

  return (
    <span suppressHydrationWarning>
      {typeof window === 'undefined' ? null : children}
    </span>
  )
}

class MyApp extends App {
  static getInitialProps = wrapper.getInitialAppProps(() => async ({ Component, ctx }) => {
    if (ctx.res) { // on the server
      const authCookie = fetchAuthCookie(ctx as unknown as GetServerSidePropsContext)

      if (!authCookie) {
        if (!ctx.asPath.startsWith('/create-account')
            && !ctx.asPath.startsWith('/login')
            && !ctx.asPath.startsWith('/logout')
            && !ctx.asPath.startsWith('/reset-password')
            && !ctx.asPath.startsWith('/invitation')
            // `/free-trial/:uuid` has to reach its own getServerSideProps so the
            // campaign id is stored before the visitor is sent to the landing page.
            && !ctx.asPath.startsWith('/free-trial')
            // SSO and Google land here with no session. The page exchanges the
            // one-time code; sending it to /login drops the code from the URL.
            && !ctx.asPath.startsWith('/auth/callback')
            && !ctx.asPath.startsWith('/auth/embed')
            && !ctx.asPath.startsWith('favicon')) {
          ctx.res.writeHead(302, { Location: '/login' })
          ctx.res.end()
        }
      } else {
        extendAuthCookie()

        const landingRoute = resolveLandingRedirect(ctx)
        if (landingRoute) {
          ctx.res.writeHead(302, { Location: landingRoute })
          ctx.res.end()
        }
      }
    }

    const pageProps = {
      ...(Component.getInitialProps ? await Component.getInitialProps(ctx) : {}),
      pathname: ctx.pathname,
    }

    return { pageProps }
  })

  render() {
    const { Component, pageProps } = this.props

    return (
      <ErrorBoundary>
        <SafeHydrate>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <BrandingThemeProvider>
                <RTLSupport />
                <ViewAsTabSync />
                <Component {...pageProps} />
                <FreeTrialEnrollment />
                <FreeTrialBanner />
                <Analytics />
              </BrandingThemeProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </SafeHydrate>
      </ErrorBoundary>
    )
  }
}

export default wrapper.withRedux(appWithTranslation(MyApp, nextI18nextConfig))

SafeHydrate.propTypes = {
  children: PropTypes.node.isRequired,
}
